import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import * as XLSX from "xlsx";
import { CsvImport } from "@/components/CsvImport";
import { CalloutImport } from "@/components/CalloutImport";

const norm = (h) => (h || "").toString().toLowerCase().replace(/[^a-z0-9]/g, "");
const GUEST_HEADERS = new Set(["guestname", "name", "guest", "party", "partyname"]);
const DATE_HEADERS = new Set(["date", "day", "calloutdate"]);
const TYPE_HEADERS = new Set(["type", "reason", "category", "calltype"]);
const CALLOUT_SIGNALS = new Set(["points", "point", "pts", "paytype", "pay", "hourspaid", "paidhours", "shifthours", "minuteslate", "fmlahours", "fmla", "sickpay", "fmlasickpay", "nonsick", "fmlanonsick", "totalfmlause", "sickhours", "vacationhours"]);

async function readRows(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith(".csv")) {
    const text = await file.text();
    return text.replace(/\r\n/g, "\n").split("\n").filter((l) => l.trim()).map((l) => l.split(","));
  }
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  return XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: "" });
}

// Decide whether a spreadsheet is a call-out/points sheet or a tour/guest sheet.
// Strong call-out signals (FMLA/points/pay columns) win even if a "Name" column exists.
function detectKind(rows) {
  if (!rows || rows.length < 1) return "tour";
  const heads = (rows[0] || []).map((h) => norm(h));
  const hasDate = heads.some((h) => DATE_HEADERS.has(h));
  const strongCallout = heads.some((h) => h.includes("fmla")) || heads.some((h) => CALLOUT_SIGNALS.has(h));
  if (hasDate && strongCallout) return "callout";
  if (heads.some((h) => GUEST_HEADERS.has(h))) return "tour";
  if (hasDate && heads.some((h) => TYPE_HEADERS.has(h))) return "callout";
  return "tour";
}

// One upload for everything: tours & guests, or call-outs / points & FMLA history.
export const DataImport = ({ onToursImported, onCalloutsImported }) => {
  const inputRef = useRef();
  const csvRef = useRef();
  const calloutRef = useRef();
  const [reading, setReading] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!/\.(csv|xlsx|xls|docx|pdf)$/.test(name)) { toast.error("Use CSV, Excel, Word, or PDF."); return; }
    setReading(true);
    try {
      let kind = "tour";
      if (/\.(csv|xlsx|xls)$/.test(name)) {
        try { kind = detectKind(await readRows(file)); } catch { kind = "tour"; }
      }
      if (kind === "callout") await calloutRef.current?.processFile(file);
      else await csvRef.current?.processFile(file);
    } finally {
      setReading(false);
    }
  };

  return (
    <div className="space-y-2">
      <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls,.docx,.pdf" onChange={onFile} className="hidden" data-testid="data-import-input" />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={reading}
        className="flex w-full items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3 text-sm text-zinc-200 transition-colors hover:bg-zinc-800 disabled:opacity-60"
        data-testid="data-import-btn"
      >
        <span className="flex items-center gap-2">
          {reading ? <Loader2 className="h-4 w-4 animate-spin text-[#E0575B]" /> : <Upload className="h-4 w-4 text-[#E0575B]" />}
          {reading ? "Reading file…" : "Upload a file to import"}
        </span>
        <span className="text-xs text-zinc-400">CSV · Excel · Word · PDF</span>
      </button>
      <p className="px-1 text-xs text-zinc-500" data-testid="data-import-hint">
        You can import <b className="text-zinc-300">tours &amp; guests</b> (tour sheets, schedules, spreadsheets — even notes &amp; documents are read automatically) and your <b className="text-zinc-300">call-outs / points &amp; FMLA history</b>. It detects the right kind automatically.
      </p>
      {/* Buttonless: these provide the preview dialogs + call-out template link */}
      <CsvImport ref={csvRef} embedded onImported={onToursImported} />
      <CalloutImport ref={calloutRef} embedded onImported={onCalloutsImported} />
    </div>
  );
};
